<template>
  <div class="color-palette-container">
    <div class="header">
      <h1>Jeffrey's Advanced Color Palette Generator</h1>
      <p>
        Generate comprehensive color palettes with OKLCH color space
        calculations
      </p>
    </div>

    <div class="controls">
      <div class="input-group">
        <input
          type="text"
          id="colorInput"
          v-model="colorInput"
          placeholder="Enter base color (hex, rgb, hsl, oklch)"
          @keypress.enter="createPalette"
        />
        <button @click="createPalette">Create colors</button>
      </div>

      <div class="input-group">
        <label for="viewMode">View:</label>
        <select id="viewMode" v-model="viewMode" @change="updateViewMode">
          <option value="default">Default</option>
          <option value="row">Row</option>
          <option value="column">Column</option>
        </select>
      </div>

      <div class="input-group">
        <label for="outputFormat">Format:</label>
        <select
          id="outputFormat"
          v-model="outputFormat"
          @change="updateJsonOutput"
        >
          <option value="hex">Hex</option>
          <option value="rgb">RGB</option>
          <option value="hsl">HSL</option>
          <option value="oklch">OKLCH</option>
        </select>
      </div>

      <button class="dark-mode-toggle" @click="toggleDarkMode">
        {{ isDarkMode ? "☀️ Light Mode" : "🌙 Dark Mode" }}
      </button>
    </div>

    <div class="palette-container">
      <div v-for="palette in palettes" :key="palette.id" class="palette-row">
        <div class="palette-header">
          <div
            class="base-color-indicator"
            :style="{
              backgroundColor: formatColor(palette.baseColorOklch, 'hex'),
            }"
          ></div>
          <input
            type="text"
            class="palette-name"
            :value="palette.name"
            @change="updatePaletteName(palette.uid, $event.target.value)"
          />
          <span class="base-color-text">{{ palette.baseColor }}</span>
          <button class="remove-palette" @click="removePalette(palette)">
            Remove
          </button>
        </div>
        <div :class="['color-grid', `${viewMode}-view`]">
          <div
            v-for="colorName in colorNames"
            :key="colorName"
            class="color-swatch"
            @click="copyColor(formatColor(palette.colors[colorName], 'hex'))"
          >
            <div
              class="color-preview"
              :style="{
                backgroundColor: formatColor(palette.colors[colorName], 'hex'),
              }"
            ></div>
            <div class="color-info">
              <div class="color-name">{{ colorName }}</div>
              <div class="color-value">
                {{ formatColor(palette.colors[colorName], "hex") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="json-output">
      <div class="json-header">
        <h3>Design Tokens (JSON)</h3>
        <button @click="copyJsonOutput">Copy JSON</button>
      </div>
      <textarea
        id="jsonOutput"
        class="json-textarea"
        :value="jsonOutput"
        readonly
      ></textarea>
    </div>

    <div :class="['copy-feedback', { show: showCopyFeedback }]">
      Copied to clipboard!
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "@/firebase";
import { mapState } from "vuex";
export default {
  name: "ColorPallette",
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
  },
  data() {
    return {
      palettes: [],
      paletteCounter: 0,
      isDarkMode: false,
      colorInput: "#3b82f6",
      viewMode: "default",
      outputFormat: "hex",
      jsonOutput: "",
      showCopyFeedback: false,
      colorNames: [
        "bg-document",
        "bg-subtle",
        "bg-default",
        "bg-hover",
        "bg-active",
        "border-subtle",
        "border-default",
        "border-hover",
        "border-active",
        "color-default",
        "color-hover",
        "color-active",
        "color-subtle",
        "color-document",
        "inverse-bg-document",
        "inverse-bg-subtle",
        "inverse-bg-default",
        "inverse-bg-hover",
        "inverse-bg-active",
        "inverse-border-subtle",
        "inverse-border-default",
        "inverse-border-hover",
        "inverse-border-active",
        "inverse-color-default",
        "inverse-color-hover",
        "inverse-color-active",
        "inverse-color-subtle",
        "inverse-color-document",
      ],
    };
  },
  mounted() {
    this.fetchPalettes();
    this.updateDarkMode();
  },
  methods: {
    async fetchPalettes() {
      const querySnapshot = await getDocs(
        collection(db, "users", this.user.uid, "color-palettes")
      );
      this.palettes = [];
      querySnapshot.docs.forEach((doc) => {
        this.palettes.push(doc.data());
      });
    },
    parseColor(input) {
      const trimmed = input.trim().toLowerCase();

      // Hex
      if (trimmed.match(/^#[0-9a-f]{3,8}$/)) {
        return this.hexToOklch(trimmed);
      }

      // RGB
      const rgbMatch = trimmed.match(
        /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/
      );
      if (rgbMatch) {
        return this.rgbToOklch(
          parseInt(rgbMatch[1]),
          parseInt(rgbMatch[2]),
          parseInt(rgbMatch[3])
        );
      }

      // HSL
      const hslMatch = trimmed.match(
        /hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/
      );
      if (hslMatch) {
        return this.hslToOklch(
          parseInt(hslMatch[1]),
          parseInt(hslMatch[2]),
          parseInt(hslMatch[3])
        );
      }

      // OKLCH
      const oklchMatch = trimmed.match(
        /oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*\)/
      );
      if (oklchMatch) {
        return {
          l: parseFloat(oklchMatch[1]),
          c: parseFloat(oklchMatch[2]),
          h: parseFloat(oklchMatch[3]),
        };
      }

      throw new Error("Invalid color format");
    },

    hexToOklch(hex) {
      const rgb = this.hexToRgb(hex);
      return this.rgbToOklch(rgb.r, rgb.g, rgb.b);
    },

    hexToRgb(hex) {
      let r, g, b;
      if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
      } else {
        r = parseInt(hex.slice(1, 3), 16);
        g = parseInt(hex.slice(3, 5), 16);
        b = parseInt(hex.slice(5, 7), 16);
      }
      return { r, g, b };
    },

    rgbToOklch(r, g, b) {
      // Convert RGB to linear RGB
      const rLin =
        r / 255 <= 0.04045
          ? r / 255 / 12.92
          : Math.pow((r / 255 + 0.055) / 1.055, 2.4);
      const gLin =
        g / 255 <= 0.04045
          ? g / 255 / 12.92
          : Math.pow((g / 255 + 0.055) / 1.055, 2.4);
      const bLin =
        b / 255 <= 0.04045
          ? b / 255 / 12.92
          : Math.pow((b / 255 + 0.055) / 1.055, 2.4);

      // Convert to OKLAB
      const lVal =
        0.4122214708 * rLin + 0.5363325363 * gLin + 0.0514459929 * bLin;
      const mVal =
        0.2119034982 * rLin + 0.6806995451 * gLin + 0.1073969566 * bLin;
      const sVal =
        0.0883024619 * rLin + 0.2817188376 * gLin + 0.6299787005 * bLin;

      const lCube = Math.cbrt(lVal);
      const mCube = Math.cbrt(mVal);
      const sCube = Math.cbrt(sVal);

      const L =
        0.2104542553 * lCube + 0.793617785 * mCube - 0.0040720468 * sCube;
      const aVal =
        1.9779984951 * lCube - 2.428592205 * mCube + 0.4505937099 * sCube;
      const bVal =
        0.0259040371 * lCube + 0.7827717662 * mCube - 0.808675766 * sCube;

      // Convert to OKLCH
      const C = Math.sqrt(aVal * aVal + bVal * bVal);
      let H = (Math.atan2(bVal, aVal) * 180) / Math.PI;
      if (H < 0) H += 360;

      return { l: L, c: C, h: H };
    },

    hslToOklch(h, s, l) {
      // Convert HSL to RGB first
      s /= 100;
      l /= 100;

      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
      const m = l - c / 2;

      let rVal, gVal, bVal;
      if (h >= 0 && h < 60) {
        rVal = c;
        gVal = x;
        bVal = 0;
      } else if (h >= 60 && h < 120) {
        rVal = x;
        gVal = c;
        bVal = 0;
      } else if (h >= 120 && h < 180) {
        rVal = 0;
        gVal = c;
        bVal = x;
      } else if (h >= 180 && h < 240) {
        rVal = 0;
        gVal = x;
        bVal = c;
      } else if (h >= 240 && h < 300) {
        rVal = x;
        gVal = 0;
        bVal = c;
      } else {
        rVal = c;
        gVal = 0;
        bVal = x;
      }

      rVal = Math.round((rVal + m) * 255);
      gVal = Math.round((gVal + m) * 255);
      bVal = Math.round((bVal + m) * 255);

      return this.rgbToOklch(rVal, gVal, bVal);
    },

    oklchToRgb(l, c, h) {
      // Convert OKLCH to OKLAB
      const hRad = (h * Math.PI) / 180;
      const aVal = c * Math.cos(hRad);
      const bVal = c * Math.sin(hRad);

      // Convert OKLAB to linear RGB
      const lCube = l + 0.3963377774 * aVal + 0.2158037573 * bVal;
      const mCube = l - 0.1055613458 * aVal - 0.0638541728 * bVal;
      const sCube = l - 0.0894841775 * aVal - 1.291485548 * bVal;

      const lLin = lCube * lCube * lCube;
      const mLin = mCube * mCube * mCube;
      const sLin = sCube * sCube * sCube;

      let rLin =
        4.0767416621 * lLin - 3.3077115913 * mLin + 0.2309699292 * sLin;
      let gLin =
        -1.2684380046 * lLin + 2.6097574011 * mLin - 0.3413193965 * sLin;
      let bLin =
        -0.0041960863 * lLin - 0.7034186147 * mLin + 1.707614701 * sLin;

      // Convert to sRGB
      rLin =
        rLin <= 0.0031308
          ? 12.92 * rLin
          : 1.055 * Math.pow(rLin, 1 / 2.4) - 0.055;
      gLin =
        gLin <= 0.0031308
          ? 12.92 * gLin
          : 1.055 * Math.pow(gLin, 1 / 2.4) - 0.055;
      bLin =
        bLin <= 0.0031308
          ? 12.92 * bLin
          : 1.055 * Math.pow(bLin, 1 / 2.4) - 0.055;

      const rFinal = Math.max(0, Math.min(255, Math.round(rLin * 255)));
      const gFinal = Math.max(0, Math.min(255, Math.round(gLin * 255)));
      const bFinal = Math.max(0, Math.min(255, Math.round(bLin * 255)));

      return { r: rFinal, g: gFinal, b: bFinal };
    },

    oklchToHex(l, c, h) {
      const { r, g, b } = this.oklchToRgb(l, c, h);
      return (
        "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
      );
    },

    oklchToHsl(l, c, h) {
      const { r, g, b } = this.oklchToRgb(l, c, h);
      const rNorm = r / 255;
      const gNorm = g / 255;
      const bNorm = b / 255;

      const max = Math.max(rNorm, gNorm, bNorm);
      const min = Math.min(rNorm, gNorm, bNorm);
      const diff = max - min;

      let hueVal = 0;
      if (diff !== 0) {
        if (max === rNorm) hueVal = ((gNorm - bNorm) / diff) % 6;
        else if (max === gNorm) hueVal = (bNorm - rNorm) / diff + 2;
        else hueVal = (rNorm - gNorm) / diff + 4;
      }
      hueVal = Math.round(hueVal * 60);
      if (hueVal < 0) hueVal += 360;

      const lightness = (max + min) / 2;
      const saturation =
        diff === 0 ? 0 : diff / (1 - Math.abs(2 * lightness - 1));

      return {
        h: hueVal,
        s: Math.round(saturation * 100),
        l: Math.round(lightness * 100),
      };
    },

    getContrastRatio(color1, color2) {
      const lum1 = this.getLuminance(color1);
      const lum2 = this.getLuminance(color2);
      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);
      return (brightest + 0.05) / (darkest + 0.05);
    },

    getLuminance(oklch) {
      const rgb = this.oklchToRgb(oklch.l, oklch.c, oklch.h);
      const rLin =
        rgb.r / 255 <= 0.03928
          ? rgb.r / 255 / 12.92
          : Math.pow((rgb.r / 255 + 0.055) / 1.055, 2.4);
      const gLin =
        rgb.g / 255 <= 0.03928
          ? rgb.g / 255 / 12.92
          : Math.pow((rgb.g / 255 + 0.055) / 1.055, 2.4);
      const bLin =
        rgb.b / 255 <= 0.03928
          ? rgb.b / 255 / 12.92
          : Math.pow((rgb.b / 255 + 0.055) / 1.055, 2.4);
      return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
    },

    adjustColorForContrast(baseColor, targetBg, minContrast) {
      let adjustedColor = { ...baseColor };
      let contrast = this.getContrastRatio(adjustedColor, targetBg);

      if (contrast < minContrast) {
        const step = 0.01;
        const maxAttempts = 100;
        let attempts = 0;

        while (contrast < minContrast && attempts < maxAttempts) {
          if (adjustedColor.l > 0.5) {
            adjustedColor.l = Math.max(0, adjustedColor.l - step);
          } else {
            adjustedColor.l = Math.min(1, adjustedColor.l + step);
          }
          contrast = this.getContrastRatio(adjustedColor, targetBg);
          attempts++;
        }
      }

      return adjustedColor;
    },

    generatePalette(baseColor) {
      const base = this.parseColor(baseColor);
      const colors = {};

      // Background colors
      colors["bg-document"] = {
        l: this.isDarkMode ? 0.12 : 0.98,
        c: base.c * 0.1,
        h: base.h,
      };
      colors["bg-subtle"] = {
        l: this.isDarkMode ? 0.15 : 0.95,
        c: base.c * 0.15,
        h: base.h,
      };
      colors["bg-default"] = {
        l: this.isDarkMode ? 0.18 : 0.92,
        c: base.c * 0.2,
        h: base.h,
      };
      colors["bg-hover"] = {
        l: this.isDarkMode ? 0.22 : 0.88,
        c: base.c * 0.25,
        h: base.h,
      };
      colors["bg-active"] = {
        l: this.isDarkMode ? 0.26 : 0.84,
        c: base.c * 0.3,
        h: base.h,
      };

      // Border colors
      colors["border-subtle"] = {
        l: this.isDarkMode ? 0.35 : 0.75,
        c: base.c * 0.4,
        h: base.h,
      };
      colors["border-default"] = this.adjustColorForContrast(
        base,
        colors["bg-default"],
        3
      );
      colors["border-hover"] = this.adjustColorForContrast(
        {
          l: colors["border-default"].l - (this.isDarkMode ? -0.1 : 0.1),
          c: colors["border-default"].c,
          h: colors["border-default"].h,
        },
        colors["bg-hover"],
        3
      );
      colors["border-active"] = this.adjustColorForContrast(
        {
          l: colors["border-hover"].l - (this.isDarkMode ? -0.1 : 0.1),
          c: colors["border-hover"].c,
          h: colors["border-hover"].h,
        },
        colors["bg-active"],
        3
      );

      // Text colors
      colors["color-default"] = this.adjustColorForContrast(
        base,
        colors["bg-subtle"],
        4.5
      );
      colors["color-hover"] = {
        l: colors["color-default"].l - (this.isDarkMode ? -0.1 : 0.1),
        c: colors["color-default"].c,
        h: colors["color-default"].h,
      };
      colors["color-active"] = {
        l: colors["color-hover"].l - (this.isDarkMode ? -0.1 : 0.1),
        c: colors["color-hover"].c,
        h: colors["color-hover"].h,
      };
      colors["color-subtle"] = this.adjustColorForContrast(
        {
          l: colors["color-default"].l + (this.isDarkMode ? -0.15 : 0.15),
          c: colors["color-default"].c * 0.8,
          h: colors["color-default"].h,
        },
        colors["bg-subtle"],
        4.5
      );
      colors["color-document"] = {
        l: this.isDarkMode ? 0.85 : 0.15,
        c: base.c * 0.6,
        h: base.h,
      };

      // Inverse colors
      colors["inverse-bg-document"] = {
        l: this.isDarkMode ? 0.95 : 0.15,
        c: base.c * 0.1,
        h: base.h,
      };
      colors["inverse-bg-subtle"] = {
        l: this.isDarkMode ? 0.88 : 0.22,
        c: base.c * 0.2,
        h: base.h,
      };
      colors["inverse-bg-default"] = this.adjustColorForContrast(
        base,
        colors["bg-document"],
        4.5
      );
      colors["inverse-bg-hover"] = {
        l: colors["inverse-bg-default"].l - (this.isDarkMode ? 0.1 : -0.1),
        c: colors["inverse-bg-default"].c,
        h: colors["inverse-bg-default"].h,
      };
      colors["inverse-bg-active"] = {
        l: colors["inverse-bg-hover"].l - (this.isDarkMode ? 0.1 : -0.1),
        c: colors["inverse-bg-hover"].c,
        h: colors["inverse-bg-hover"].h,
      };

      colors["inverse-border-subtle"] = {
        l:
          (colors["inverse-bg-active"].l +
            (colors["inverse-border-default"]?.l || 0.5)) /
          2,
        c: base.c * 0.4,
        h: base.h,
      };
      colors["inverse-border-default"] = this.adjustColorForContrast(
        { l: 0.5, c: base.c * 0.5, h: base.h },
        colors["inverse-bg-default"],
        3
      );
      colors["inverse-border-hover"] = this.adjustColorForContrast(
        {
          l:
            colors["inverse-border-default"].l + (this.isDarkMode ? 0.1 : -0.1),
          c: colors["inverse-border-default"].c,
          h: colors["inverse-border-default"].h,
        },
        colors["inverse-bg-hover"],
        3
      );
      colors["inverse-border-active"] = this.adjustColorForContrast(
        {
          l: colors["inverse-border-hover"].l + (this.isDarkMode ? 0.1 : -0.1),
          c: colors["inverse-border-hover"].c,
          h: colors["inverse-border-hover"].h,
        },
        colors["inverse-bg-active"],
        3
      );

      // Inverse text colors
      const bgDocumentForText = colors["bg-document"];
      colors["inverse-color-default"] =
        this.getContrastRatio(
          bgDocumentForText,
          colors["inverse-bg-default"]
        ) >= 4.5
          ? bgDocumentForText
          : { l: this.isDarkMode ? 0.1 : 0.95, c: 0, h: 0 };
      colors["inverse-color-hover"] =
        this.getContrastRatio(bgDocumentForText, colors["inverse-bg-hover"]) >=
        4.5
          ? bgDocumentForText
          : { l: this.isDarkMode ? 0.1 : 0.95, c: 0, h: 0 };
      colors["inverse-color-active"] =
        this.getContrastRatio(bgDocumentForText, colors["inverse-bg-active"]) >=
        4.5
          ? bgDocumentForText
          : { l: this.isDarkMode ? 0.1 : 0.95, c: 0, h: 0 };
      colors["inverse-color-subtle"] = this.adjustColorForContrast(
        {
          l:
            colors["inverse-color-default"].l -
            (this.isDarkMode ? -0.15 : 0.15),
          c: colors["inverse-color-default"].c,
          h: colors["inverse-color-default"].h,
        },
        colors["inverse-bg-document"],
        4.5
      );
      colors["inverse-color-document"] = bgDocumentForText;

      return colors;
    },

    formatColor(oklch, format) {
      switch (format) {
        case "hex":
          return this.oklchToHex(oklch.l, oklch.c, oklch.h);
        case "rgb": {
          const { r, g, b } = this.oklchToRgb(oklch.l, oklch.c, oklch.h);
          return `rgb(${r}, ${g}, ${b})`;
        }
        case "hsl": {
          const { h, s, l } = this.oklchToHsl(oklch.l, oklch.c, oklch.h);
          return `hsl(${h}, ${s}%, ${l}%)`;
        }
        case "oklch":
          return `oklch(${oklch.l.toFixed(3)} ${oklch.c.toFixed(
            3
          )} ${oklch.h.toFixed(1)})`;
        default:
          return this.oklchToHex(oklch.l, oklch.c, oklch.h);
      }
    },

    async createPalette() {
      if (!this.colorInput.trim()) return;

      try {
        const baseColor = this.parseColor(this.colorInput);
        const palette = this.generatePalette(this.colorInput);

        this.paletteCounter++;
        const paletteData = {
          id: this.paletteCounter,
          name: `Palette ${this.paletteCounter}`,
          baseColor: this.colorInput,
          baseColorOklch: baseColor,
          colors: palette,
        };

        const docRef = await addDoc(
          collection(db, "users", this.user.uid, "color-palettes"),
          paletteData
        );
        await updateDoc(docRef, {
          uid: docRef.id,
        });
        paletteData.uid = docRef.id;
        this.palettes.push(paletteData);
        this.updateJsonOutput();
        // Scroll to bottom of page after adding a new palette
        this.$nextTick(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        });
      } catch (error) {
        alert(
          "Invalid color format. Please use hex, rgb, hsl, or oklch format."
        );
      }
    },

    updatePaletteName(uid, newName) {
      const palette = this.palettes.find((p) => p.uid === uid);
      if (palette) {
        palette.name = newName;
        this.updateJsonOutput();
      }
    },

    async removePalette(palette) {
      console.log(palette);
      this.palettes = this.palettes.filter((p) => p.uid !== palette.uid);
      this.updateJsonOutput();
      await deleteDoc(
        doc(db, "users", this.user.uid, "color-palettes", palette.uid)
      );
    },

    updateViewMode() {
      // View mode is reactive, no need to do anything
    },

    updateJsonOutput() {
      const tokens = {};

      this.palettes.forEach((palette) => {
        tokens[palette.name] = {};
        this.colorNames.forEach((colorName) => {
          const color = palette.colors[colorName];
          tokens[palette.name][colorName] = {
            $type: "color",
            $value: this.formatColor(color, this.outputFormat),
          };
        });
      });

      this.jsonOutput = JSON.stringify(tokens, null, 2);
    },

    copyColor(colorValue) {
      navigator.clipboard.writeText(colorValue).then(() => {
        this.displayCopyFeedback();
      });
    },

    copyJsonOutput() {
      navigator.clipboard.writeText(this.jsonOutput).then(() => {
        this.displayCopyFeedback();
      });
    },

    displayCopyFeedback() {
      this.showCopyFeedback = true;
      setTimeout(() => {
        this.showCopyFeedback = false;
      }, 2000);
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      this.updateDarkMode();

      // Regenerate all palettes with new dark mode settings
      this.palettes.forEach((palette) => {
        palette.colors = this.generatePalette(palette.baseColor);
      });

      this.updateJsonOutput();
    },

    updateDarkMode() {
      if (this.isDarkMode) {
        document.body.setAttribute("data-theme", "dark");
      } else {
        document.body.removeAttribute("data-theme");
      }
    },
  },
};
</script>

<style scoped>
.color-palette-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  padding: 20px;
  background: var(--bg-secondary, #f8f9fa);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.input-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

input,
select,
button {
  height: 44px;
  padding: 0 16px;
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary, #ffffff);
  color: var(--text-primary, #212529);
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover),
    border-color var(--dur-fast) var(--ease-hover),
    box-shadow var(--dur-fast) var(--ease-hover),
    transform var(--dur-press) var(--ease-out);
}

input:focus,
select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
}

button {
  background: #007bff;
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

button:hover {
  background: #0056b3;
}

button:active {
  transform: translateY(1px);
}

.dark-mode-toggle {
  background: var(--text-secondary, #6c757d);
  margin-left: auto;
}

.palette-container {
  margin-bottom: 30px;
}

.palette-row {
  margin-bottom: 30px;
  padding: 20px;
  background: var(--bg-secondary, #f8f9fa);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.palette-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.base-color-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--border-color, #dee2e6);
}

.palette-name {
  background: transparent;
  border: none;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #212529);
  border-bottom: 1px solid transparent;
  padding: 4px 0;
}

.palette-name:hover {
  border-bottom-color: var(--border-color, #dee2e6);
}

.base-color-text {
  font-size: 12px;
  color: var(--text-secondary, #6c757d);
  font-family: monospace;
}

.remove-palette {
  background: #dc3545;
  margin-left: auto;
  padding: 8px 12px;
  font-size: 12px;
  height: auto;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
}

.color-grid.row-view {
  display: flex;
  overflow-x: auto;
  gap: 15px;
  padding-bottom: 10px;
}

.color-grid.row-view .color-swatch {
  min-width: 120px;
  flex-shrink: 0;
}

.color-grid.column-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.color-swatch {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.color-swatch:hover {
  transform: translateY(-2px);
}

.color-preview {
  height: 80px;
  width: 100%;
}

.color-info {
  padding: 12px;
  background: var(--bg-primary, #ffffff);
}

.color-name {
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 4px;
  color: var(--text-primary, #212529);
}

.color-value {
  font-family: "Courier New", monospace;
  font-size: 11px;
  color: var(--text-secondary, #6c757d);
  word-break: break-all;
}

.json-output {
  margin-top: 30px;
}

.json-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.json-textarea {
  width: 100%;
  height: 300px;
  padding: 16px;
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 8px;
  font-family: "Courier New", monospace;
  font-size: 12px;
  background: var(--bg-primary, #ffffff);
  color: var(--text-primary, #212529);
  resize: vertical;
}

.copy-feedback {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #28a745;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  transform: translateX(100%);
  transition: transform 0.3s ease;
}

.copy-feedback.show {
  transform: translateX(0);
}

@media (max-width: 768px) {
  .controls {
    flex-direction: column;
  }

  .input-group {
    width: 100%;
    justify-content: center;
  }

  .color-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

<style>
:root {
  --bg-primary: #ffffff;
  --bg-secondary: #f8f9fa;
  --text-primary: #212529;
  --text-secondary: #6c757d;
  --border-color: #dee2e6;
  --shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

[data-theme="dark"] {
  --bg-primary: #1a1a1a;
  --bg-secondary: #2d2d2d;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border-color: #404040;
  --shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: var(--bg-primary);
  color: var(--text-primary);
  line-height: 1.6;
  transition: background var(--dur-base) var(--ease-hover),
    color var(--dur-base) var(--ease-hover);
}
</style>
